/**
 * Every fact about the company that appears on the site, in one place.
 *
 * ─── THE NAME ──────────────────────────────────────────────────────────────────
 * Trading name: SECOND SHIFT CLEANING (decided 2026-08-28).
 *
 * It replaces "Core Cleaning Tampa" / "Core Cleans", which are blocked by a live
 * federal trademark registration — US Reg. 7,887,793, wordmark CORE CLEAN, class 037
 * (commercial and medical janitorial), owned by Core Clean, LLC of South Carolina,
 * whose own site lists Tampa and St. Petersburg as service areas. Full evidence and
 * the rename checklist: second-shift-cleaning/docs/BRAND-AUDIT.md.
 *
 * The LEGAL entity name is unchanged — Tampa Core Cleaning Solutions, LLC keeps its
 * EIN, its bank account, and every signed document. Only the trading name changes,
 * via a fictitious-name (DBA) filing.
 * ───────────────────────────────────────────────────────────────────────────────
 */

export const SITE = {
  name: 'Second Shift Cleaning',
  shortName: 'Second Shift',
  legalName: 'Tampa Core Cleaning Solutions, LLC',

  /**
   * Canonical origin. Registered 2026-09-01 at Cloudflare; the zone sits in the same
   * account as the Worker, so `custom_domain: true` routes manage DNS and the cert.
   *
   * `www` 301s here (see worker.ts) and the workers.dev hostname stays reachable but is
   * never canonical — every canonical, sitemap entry and OG URL points at the apex.
   */
  domain: 'secondshiftcleaning.com',
  url: 'https://secondshiftcleaning.com',

  /**
   * Where the actual software lives — NOT this site.
   *
   * This domain is marketing only. Every tenant's console is at
   * `<slug>.broomstack.app`, and Second Shift's slug is `ssc`. See the
   * `broomstack-tenant-address-convention` memory: client domains never host the
   * app, so a login link here has to leave the domain.
   *
   * Defined once, here, because three portals link to it and a fourth surface
   * (the footer) repeats them. Typing the host into each link is how one of them
   * ends up pointing at New Leaf after a copy-paste — which is precisely the
   * class of bug that had this site's leads filing under New Leaf's tenant until
   * 2026-09-04.
   */
  console: {
    origin: 'https://ssc.broomstack.app',
    /** Owners, managers, dispatchers. Since PR #24 this one login also opens the crew portal. */
    admin: 'https://ssc.broomstack.app/admin/login',
    /** Cleaners. A crew member with no admin role still logs in here. */
    team: 'https://ssc.broomstack.app/team/login',
    /** Clients viewing jobs and invoices. */
    client: 'https://ssc.broomstack.app/client/login',
  },

  founded: 'July 2025',
  location: 'Brandon, FL',
  serviceArea: 'Tampa Bay',

  contact: {
    /** No phone number is published right now: contact is email and the quote form. */
    email: 'office@secondshiftcleaning.com',
    address: 'Brandon, FL 33511',
  },

  hours: {
    office: 'Mon–Fri, 8 am – 6 pm',
    service: 'Cleaning happens after hours — evenings, nights, and weekends',
    /**
     * The same office hours in the shape schema.org wants, so the JSON-LD is derived
     * rather than hand-written alongside the prose. Times are 24h local.
     */
    officeSpec: {
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '18:00',
    },
  },

  /**
   * Where else this business exists on the internet, for schema.org `sameAs`.
   *
   * `sameAs` is how a search engine works out that the Google Business Profile, the
   * Facebook page and this site are ONE entity rather than three — which is what
   * consolidates authority instead of splitting it.
   *
   * ⚠ EMPTY ON PURPOSE. Every URL here must be a profile that actually exists and is
   * actually ours. A `sameAs` pointing at a profile we do not control, or a dead one,
   * is worse than none. `localBusinessSchema()` omits the field entirely while this is
   * empty rather than emitting `sameAs: []`.
   *
   * To fill: add the verified Google Business Profile URL, then Facebook / Yelp /
   * Apple Business Connect / Bing Places as each is claimed. Note the old Wix site and
   * the existing Yelp listing still carry a "10+ years" claim that contradicts
   * `claims` below — fix those BEFORE linking them here.
   */
  profiles: [] as readonly string[],

  geo: { lat: 27.9378, lng: -82.2859 },

  /**
   * Every city we will actually send a crew to. Drives /service-area, the city pages,
   * and the schema.org areaServed block — never hand-listed in a template.
   */
  serviceCities: [
    'Tampa',
    'Brandon',
    'Riverview',
    'St. Petersburg',
    'Clearwater',
    'Lakeland',
    'Wesley Chapel',
  ],

  /** Government and property-management onboarding forms always ask for this. */
  naics: '561720',

  /**
   * ⚠ CLAIMS GATE — every entry must be TRUE before the matching copy renders.
   *
   * The old Wix site and the Yelp listing still carry "10+ years" and "same day
   * service"; the LLC was formed in July 2025. Fabricated tenure is the fastest way to
   * lose a commercial buyer, so components read these flags and simply omit the claim
   * on a `false` rather than shipping something unverifiable.
   */
  claims: {
    /** $1M / $2M general liability. Today the policy is $300k GL only. */
    insuredToCommercialLimits: false,
    /** Workers' compensation in force. */
    workersComp: false,
    /** Certificate of insurance issued within 24 hours of request. */
    coiWithin24h: false,
    /** Background checks run on every technician. */
    backgroundChecked: false,
    /** A human answers the phone during stated hours. */
    callsAnsweredLive: false,
    /** Registered with the FL DOR (DR-1) so invoices carry sales tax correctly. */
    salesTaxRegistered: false,
    /** The written guarantee is agreed and will be honoured. */
    guaranteeCommitted: false,
    /** Written permission to name a past client on /our-work. */
    pastClientNamingPermission: false,
  },
} as const;

/**
 * The guarantee, written out.
 *
 * The 17-site competitor audit found "Guaranteed." with nothing after it on most of
 * them — which is exactly why a DEFINED one is a differentiator. Gated on
 * `claims.guaranteeCommitted` so it cannot ship before it has been agreed.
 */
export const GUARANTEE = {
  headline: 'If something gets missed, we fix it free.',
  body: 'Tell us by noon the next business day. We fix it within 24 hours — or that visit is free.',
} as const;

/**
 * The accountability claims, each tied to its gate.
 *
 * Rendered by the trust line and /why-us. A claim whose gate is false is not rendered.
 */
export const TRUST_POINTS = [
  { gate: 'insuredToCommercialLimits', text: 'Insured to $1M / $2M and bonded' },
  { gate: 'coiWithin24h', text: 'Certificate of insurance within 24 hours' },
  { gate: 'backgroundChecked', text: 'Background-checked W-2 crews — never subcontractors' },
  { gate: 'callsAnsweredLive', text: 'A person answers the phone, 7 am to 7 pm' },
] as const satisfies readonly { gate: keyof typeof SITE.claims; text: string }[];

/** The claims that are true today, in display order. */
export function activeTrustPoints() {
  return TRUST_POINTS.filter((p) => SITE.claims[p.gate]);
}
